import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from 'recharts'
import type { BenchmarkRun } from '@/types'
import { useDesignTokens } from '@/hooks/useDesignTokens'

interface Props { run: BenchmarkRun }

export function HistogramPanel({ run }: Props) {
  const tokens = useDesignTokens()

  const allTimes = run.results.filter((r) => r.success).map((r) => r.totalTimeSec)
  if (allTimes.length === 0) return null

  const min = Math.min(...allTimes)
  const max = Math.max(...allTimes)
  const range = max - min || 1
  const bucketCount = 20
  const bucketSize = range / bucketCount
  const profileNames = [...new Set(run.results.map((r) => r.profileName))]

  const buckets = Array.from({ length: bucketCount }, (_, i) => {
    const rangeStart = min + i * bucketSize
    const rangeEnd = min + (i + 1) * bucketSize
    const bucket: Record<string, number | string> = { range: `${rangeStart.toFixed(1)}-${rangeEnd.toFixed(1)}s` }
    for (const pName of profileNames) {
      bucket[pName] = run.results.filter(
        (r) => r.success && r.profileName === pName && r.totalTimeSec >= rangeStart &&
          (i === bucketCount - 1 ? r.totalTimeSec <= rangeEnd : r.totalTimeSec < rangeEnd)
      ).length
    }
    return bucket
  })

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Latency Distribution</h3>
      <div style={{ color: '#8888aa', fontSize: 12, marginBottom: 12 }}>
        Each bar shows how many individual benchmark calls fell in that latency bucket.
        Wider spread = less consistent performance. Use 50+ iterations for reliable distributions.
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={buckets} margin={{ left: 10, right: 20, bottom: 20 }}>
          <CartesianGrid stroke={tokens.lineGrid} strokeDasharray="3 3" />
          <XAxis dataKey="range" stroke={tokens.axisStroke} tick={{ fill: tokens.tickColor, fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
          <YAxis stroke={tokens.axisStroke} tick={{ fill: tokens.tickColor }} />
          <Tooltip
            contentStyle={{ backgroundColor: tokens.tooltipBg, border: `1px solid ${tokens.gridColor}` }}
            labelStyle={{ color: tokens.axisLabelColor }}
          />
          <Legend />
          {profileNames.map((name, i) => (
            <Bar key={name} dataKey={name} fill={tokens.seriesPalette[i % tokens.seriesPalette.length]} opacity={0.8} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: { borderRadius: 8, padding: 16, marginBottom: 16 },
  title: { margin: '0 0 12px', fontSize: 15 }
}
